import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  MenuItem,
  Popper,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { ProfileRow } from "../ProfileRow";
import { useTranslation } from "@helpers/translate";
import ArrowDownIcon from "@assets/icons/ArrowDownIcon";

import { getTimezoneOptions } from "@helpers/timezone";
import "./TimezoneRow.sass";

const DROPDOWN_GAP = 6;
const DROPDOWN_SIDE_OFFSET = 16;

const getTimezoneOffset = (timezoneName) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneName,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());

    const rawOffset =
      parts.find((part) => part.type === "timeZoneName")?.value || "GMT+0";

    return rawOffset.replace("UTC", "GMT");
  } catch (error) {
    return "GMT+0";
  }
};

const formatTimezoneLabel = (timezoneName) => {
  if (!timezoneName) {
    return "";
  }

  return `${timezoneName} (${getTimezoneOffset(timezoneName)})`;
};

const CustomPopper = (props) => {
  const modifiers = [
    ...(props.modifiers || []),
    {
      name: "eventListeners",
      enabled: true,
      options: {
        scroll: true,
        resize: true,
      },
    },
    {
      name: "offset",
      enabled: true,
      options: {
        offset: [0, DROPDOWN_GAP],
      },
    },
    {
      name: "flip",
      enabled: false,
    },
    {
      name: "preventOverflow",
      enabled: true,
      options: {
        padding: DROPDOWN_SIDE_OFFSET,
        mainAxis: true,
        altAxis: false,
        tether: false,
      },
    },
  ];

  return (
    <Popper
      {...props}
      strategy="absolute"
      placement="bottom"
      modifiers={modifiers}
      className={`${props.className || ""} timezone-select__popper`}
    />
  );
};

export const TimezoneRow = ({ timezone, onSelectTimezone }) => {
  const { __ } = useTranslation();

  const isMobile = useMediaQuery("(max-width:480px)");

  const [open, setOpen] = useState(false);

  const selectContainerRef = useRef(null);
  const triggerButtonRef = useRef(null);

  const timezones = useMemo(() => {
    const supportedTimezones = getTimezoneOptions().map((option) => option.timezone);

    if (timezone && !supportedTimezones.includes(timezone)) {
      return [timezone, ...supportedTimezones];
    }

    return supportedTimezones;
  }, [timezone]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const DIRECTION_THRESHOLD = 6;
    const WHEEL_GESTURE_DELAY = 150;

    let touchStartY = null;
    let touchGestureDirection = null;

    let wheelGestureDirection = null;
    let wheelGestureTimer = null;

    const isInsideTimezoneSelect = (target) => {
      if (!(target instanceof Element)) {
        return false;
      }

      const clickedInsideSelect = selectContainerRef.current?.contains(target);

      const clickedTrigger = triggerButtonRef.current?.contains(target);

      const clickedDropdown = Boolean(
        target.closest(".timezone-select__popper"),
      );

      return clickedInsideSelect || clickedTrigger || clickedDropdown;
    };

    const handleClickOutside = (event) => {
      if (isInsideTimezoneSelect(event.target)) {
        return;
      }

      setOpen(false);
    };

    const resetWheelGesture = () => {
      wheelGestureDirection = null;
      wheelGestureTimer = null;
    };

    const handleWheelOutside = (event) => {
      if (isInsideTimezoneSelect(event.target)) {
        return;
      }

      clearTimeout(wheelGestureTimer);

      wheelGestureTimer = setTimeout(resetWheelGesture, WHEEL_GESTURE_DELAY);

      /*
       * Ігноруємо дуже маленькі значення, які можуть
       * виникати через інерцію або відскок на межі сторінки.
       */
      if (Math.abs(event.deltaY) < DIRECTION_THRESHOLD) {
        return;
      }

      /*
       * Визначаємо напрямок лише на початку жесту.
       * Подальший інерційний відскок його вже не змінить.
       */
      if (!wheelGestureDirection) {
        wheelGestureDirection = event.deltaY < 0 ? "up" : "down";
      }

      if (wheelGestureDirection === "up") {
        setOpen(false);
      }
    };

    const handleTouchStartOutside = (event) => {
      if (isInsideTimezoneSelect(event.target)) {
        touchStartY = null;
        touchGestureDirection = null;
        return;
      }

      touchStartY = event.touches[0]?.clientY ?? null;
      touchGestureDirection = null;
    };

    const handleTouchMoveOutside = (event) => {
      if (isInsideTimezoneSelect(event.target)) {
        return;
      }

      const currentTouchY = event.touches[0]?.clientY;

      if (
        typeof currentTouchY !== "number" ||
        typeof touchStartY !== "number"
      ) {
        return;
      }

      const touchDelta = currentTouchY - touchStartY;

      if (Math.abs(touchDelta) < DIRECTION_THRESHOLD) {
        return;
      }

      /*
       * Палець рухається вниз — сторінка прокручується вгору.
       * Палець рухається вгору — сторінка прокручується вниз.
       */
      if (!touchGestureDirection) {
        touchGestureDirection = touchDelta > 0 ? "up" : "down";
      }

      if (touchGestureDirection === "up") {
        setOpen(false);
      }
    };

    const handleTouchEnd = () => {
      touchStartY = null;
      touchGestureDirection = null;
    };

    document.addEventListener("mousedown", handleClickOutside);

    document.addEventListener("wheel", handleWheelOutside, {
      capture: true,
      passive: true,
    });

    document.addEventListener("touchstart", handleTouchStartOutside, {
      capture: true,
      passive: true,
    });

    document.addEventListener("touchmove", handleTouchMoveOutside, {
      capture: true,
      passive: true,
    });

    document.addEventListener("touchend", handleTouchEnd, true);
    document.addEventListener("touchcancel", handleTouchEnd, true);

    return () => {
      clearTimeout(wheelGestureTimer);

      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("wheel", handleWheelOutside, true);
      document.removeEventListener("touchstart", handleTouchStartOutside, true);
      document.removeEventListener("touchmove", handleTouchMoveOutside, true);
      document.removeEventListener("touchend", handleTouchEnd, true);
      document.removeEventListener("touchcancel", handleTouchEnd, true);
    };
  }, [open]);

  const handleTimezoneChange = (event, nextTimezone) => {
    if (!nextTimezone) {
      return;
    }

    onSelectTimezone(nextTimezone);
    setOpen(false);
  };

  const handleToggleDropdown = () => {
    setOpen((previousOpen) => !previousOpen);
  };

  const handleMobilePointerDown = (event) => {
    if (!isMobile) {
      return;
    }

    /*
     * Не дозволяємо браузеру сфокусувати MUI input.
     * Саме фокус input спричиняє zoom на мобільному.
     */
    event.preventDefault();

    const activeElement = document.activeElement;

    if (activeElement && typeof activeElement.blur === "function") {
      activeElement.blur();
    }

    setOpen(true);
  };

  return (
    <div className="timezone-row">
      <ProfileRow
        label={__("profile.timezone")}
        value={
          <div
            ref={selectContainerRef}
            className="profile-row__edit-input profile-row__timezone-input"
            onPointerDown={handleMobilePointerDown}
          >
            <Autocomplete
              options={timezones}
              value={timezone || null}
              onChange={handleTimezoneChange}
              isOptionEqualToValue={(option, currentValue) =>
                option === currentValue
              }
              getOptionLabel={formatTimezoneLabel}
              open={open}
              onOpen={() => setOpen(true)}
              onClose={(event, reason) => {
                if (isMobile && reason === "blur") {
                  return;
                }

                setOpen(false);
              }}
              openOnFocus={!isMobile}
              disableClearable
              blurOnSelect
              forcePopupIcon={false}
              className="bo-select-timezone timezone-select"
              ListboxProps={{
                className: "timezone-select__listbox",
              }}
              renderOption={(optionProps, option) => {
                const { key, className, ...restOptionProps } = optionProps;

                return (
                  <MenuItem
                    {...restOptionProps}
                    key={key || option}
                    className={`${className || ""} timezone-select__option`}
                  >
                    {formatTimezoneLabel(option)}
                  </MenuItem>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  className="bo-select-timezone-input timezone-select__input"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    readOnly: isMobile,
                    inputMode: isMobile ? "none" : params.inputProps.inputMode,
                    tabIndex: isMobile ? -1 : params.inputProps.tabIndex,
                    autoComplete: "off",

                    onMouseDown: (event) => {
                      if (isMobile) {
                        event.preventDefault();
                        return;
                      }

                      params.inputProps.onMouseDown?.(event);
                    },

                    onTouchStart: (event) => {
                      if (isMobile) {
                        event.preventDefault();
                        return;
                      }

                      params.inputProps.onTouchStart?.(event);
                    },

                    onFocus: (event) => {
                      if (isMobile) {
                        event.preventDefault();
                        event.currentTarget.blur();
                        return;
                      }

                      params.inputProps.onFocus?.(event);
                    },
                  }}
                />
              )}
              PopperComponent={CustomPopper}
            />
          </div>
        }
        actions={
          <button
            ref={triggerButtonRef}
            type="button"
            className="profile-action__button timezone-select__trigger"
            onClick={handleToggleDropdown}
            aria-label={__("profile.timezone")}
            aria-expanded={open}
          >
            <span
              className={`timezone-select__arrow ${
                open ? "timezone-select__arrow--open" : ""
              }`}
              aria-hidden="true"
            >
              <ArrowDownIcon size={16} />
            </span>
          </button>
        }
      />
    </div>
  );
};

export default TimezoneRow;
